/**
 * OrderStatistics Component
 *
 * Displays key metrics in stat cards
 */

import React, { useMemo } from 'react';
import { FaDollarSign } from 'react-icons/fa';
import { IoDocumentText, IoCheckmarkCircleOutline, IoCloseCircleOutline, IoTimeOutline, IoConstructOutline } from 'react-icons/io5';

import { StatisticCard } from '@/components/statisticCard';
import { OrderStatus, ORDER_STATUS_LABELS, ORDER_STATUS_HEX_COLORS } from '../constants';
import { IOrderStatusCount } from '../types';

interface OrderStatisticsProps {
    loading?: boolean;
    statusCounts?: IOrderStatusCount[];
    totalCount?: number;
    totalValue?: number;
}

interface StatusConfig {
    statuscode: number;
    label: string;
    icon: React.ReactNode;
    color: string;
}

const ALL_STATUSES: StatusConfig[] = [
    {
        statuscode: OrderStatus.AWAITING_PAYMENT,
        label: ORDER_STATUS_LABELS[OrderStatus.AWAITING_PAYMENT],
        icon: <IoTimeOutline size={14} />,
        color: ORDER_STATUS_HEX_COLORS[OrderStatus.AWAITING_PAYMENT],
    },
    {
        statuscode: OrderStatus.IN_PRODUCTION,
        label: ORDER_STATUS_LABELS[OrderStatus.IN_PRODUCTION],
        icon: <IoDocumentText size={14} />,
        color: ORDER_STATUS_HEX_COLORS[OrderStatus.IN_PRODUCTION],
    },
    {
        statuscode: OrderStatus.READY_TO_PRODUCTION,
        label: ORDER_STATUS_LABELS[OrderStatus.READY_TO_PRODUCTION],
        icon: <IoConstructOutline size={14} />,
        color: ORDER_STATUS_HEX_COLORS[OrderStatus.READY_TO_PRODUCTION],
    },
    {
        statuscode: OrderStatus.Completed,
        label: ORDER_STATUS_LABELS[OrderStatus.Completed],
        icon: <IoCheckmarkCircleOutline size={14} />,
        color: ORDER_STATUS_HEX_COLORS[OrderStatus.Completed],
    },
    {
        statuscode: OrderStatus.Cancelled,
        label: ORDER_STATUS_LABELS[OrderStatus.Cancelled],
        icon: <IoCloseCircleOutline size={14} />,
        color: ORDER_STATUS_HEX_COLORS[OrderStatus.Cancelled],
    },
];

export const OrderStatistics: React.FC<OrderStatisticsProps> = ({
    statusCounts,
    totalCount,
    totalValue,
    loading
}) => {
    const statusCountsMap = useMemo(() => {
        const map: Record<number, number> = {};
        statusCounts?.forEach((item) => {
            map[item.statuscode] = item.count;
        });
        return map;
    }, [statusCounts]);

    return (
        <div className="flex gap-4 overflow-x-auto">
            <StatisticCard
                title="Total Orders"
              value={totalCount ?? 0}
              icon={<IoDocumentText size={14} />}
              iconBgColor="#ef4444"
              className="min-w-[160px]"
              enableAnimation={!loading}
          />
          <StatisticCard
              title="Outstanding Payments"
              value={totalValue ?? 0}
              prefix="£"
              icon={<FaDollarSign size={14} />}
              iconBgColor="#8b1538"
              className="min-w-[220px]"
              enableAnimation={!loading}
              decimals={2}
          />
          {ALL_STATUSES.map((status) => (
              <StatisticCard
                  key={status.statuscode}
                  title={status.label}
                  value={statusCountsMap[status.statuscode] ?? 0}
                  icon={status.icon}
                  iconBgColor={status.color}
                  className="min-w-[160px]"
                  enableAnimation={!loading}
              />
      ))}
      </div>
  );
};
